<template>
  <vs-card v-if="job" :to="linkTo" no-content-padding class="overflow-hidden">
    <v-container>
      <v-row>
        <v-tooltip v-if="stopsEnabled" :open-delay="250" content-class="solid-tooltip" left>
          <template #activator="{ on }">
            <v-col
              cols="auto"
              :class="['d-flex', 'align-center', 'justify-center', stopClass]"
              v-on="on"
            >
              <v-icon data-test="stop-icon">{{ completedIcon }}</v-icon>
            </v-col>
          </template>
          <span data-test="stop-icon-tooltip">{{ completedIconTooltip }}</span>
        </v-tooltip>
        <v-col>
          <div>
            <v-row align="center">
              <v-col>
                <vs-text data-test="job-title">{{ jobTitle }}</vs-text>
              </v-col>
              <v-col class="shrink">
                <job-status-chip :status="finalStatus" />
              </v-col>
              <v-col class="shrink">
                <v-checkbox v-model="selected" hide-details class="ma-0 pa-0" @click.prevent />
              </v-col>
            </v-row>
            <v-row dense>
              <v-col>
                <vs-text type="paragraph">
                  {{ resourceName }}
                  <span aria-hidden="true"> · </span>
                  {{ activityName }}
                </vs-text>
              </v-col>
            </v-row>
            <v-row v-if="actualDropoffStop && !isRepeatRequest" dense>
              <v-icon small class="ml-1">mdi-flag</v-icon>
              <vs-text
                v-if="dropoffLocationsDiffer"
                type="caption"
                class="text-decoration-line-through ml-1"
              >
                {{ requestedDropoffStop?.locationName }}
              </vs-text>
              <v-icon v-if="dropoffLocationsDiffer" small class="ml-1">mdi-arrow-right</v-icon>
              <vs-text class="ml-1" type="caption">
                {{ actualDropoffStop.locationName }}
              </vs-text>
            </v-row>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </vs-card>
</template>

<script setup lang="ts">
import type { RouterLinkProps } from 'vue-router/types/router'
import { JobRequestJobsResponse } from '@/models/types/job-request-jobs-response'
import VsText from '@/components/vision/VsText.vue'
import JobStatusChip from '@/components/tickets/JobStatusChip.vue'
import { computed } from 'vue'
import { JobStatus, NO_APPROVAL } from '@/models/job'
import VsCard from '@/components/vision/VsCard.vue'
import { isRequestedStopCompleted } from '@/components/tickets/StopsAndInventories/useIsRequestedStopCompleted'
import { JobRequest, JobStopType } from '@ironsight/types'
import { useGetJobRequestedStopsQuery, useGetJobStopsQuery } from '@/api/jobs'

type RouterLinkTo = RouterLinkProps['to']

const props = defineProps<{
  jobRequest: JobRequest
  job: JobRequestJobsResponse
  linkTo: RouterLinkTo
  isSelected: boolean
  stopsEnabled: boolean
}>()

const emit = defineEmits<{
  (e: 'selected', jobId: string)
  (e: 'deselected', jobId: string)
}>()

const tripNumber = computed(() => props.jobRequest.jobIds.indexOf(props.job.id) + 1)
const requestedTrips = computed(() => props.jobRequest.maxExecutions)
const isRepeatRequest = computed(() => props.jobRequest.isRepeatTrip ?? false)

const finalStatus = computed(() =>
  props.job.approval_status !== null && props.job.approval_status !== NO_APPROVAL
    ? props.job.approval_status
    : props.job.status.trim(),
)

const { data: actualStops } = useGetJobStopsQuery(props.job.id)
const { data: requestedStops } = useGetJobRequestedStopsQuery(props.job.id)

const stopCompletionStatuses = computed(() => {
  if (!actualStops.value || !requestedStops.value) {
    return []
  }

  const actualStopsMap = new Map(actualStops.value.map((s) => [s.requestedStopId, s]))

  const requestedDestinationStop = requestedStops.value.find(
    (s) => s.stopType === JobStopType.DropOff,
  )
  if (!requestedDestinationStop) {
    return []
  }

  const actualDestinationStop = actualStops.value.find((s) => s.stopType === JobStopType.DropOff)

  return [
    ...requestedStops.value.map((requestedStop) =>
      isRequestedStopCompleted(requestedStop, actualStopsMap.get(requestedStop.id)),
    ),
    isRequestedStopCompleted(requestedDestinationStop, actualDestinationStop),
  ]
})

const requestedDropoffStop = computed(() =>
  requestedStops.value?.find((s) => s.stopType === JobStopType.DropOff),
)

const dropoffLocationsDiffer = computed(
  () =>
    requestedDropoffStop.value &&
    actualDropoffStop.value &&
    requestedDropoffStop.value.locationName !== actualDropoffStop.value.locationName,
)

const actualDropoffStop = computed(() =>
  actualStops.value?.find((s) => s.stopType === JobStopType.DropOff),
)

const doAllLocationsMatch = computed(() =>
  stopCompletionStatuses.value.every((s) => s.locationMatches),
)
const doesAllInventoryMatch = computed(() =>
  stopCompletionStatuses.value.every((s) => s.inventoryMatches),
)
const areAllStopsCompleted = computed(
  () => doAllLocationsMatch.value && doesAllInventoryMatch.value,
)
const isJobCompleted = computed(() => props.job.status === JobStatus.COMPLETED)

const stopClass = computed(() => {
  if (!isJobCompleted.value) {
    return 'job-not-complete'
  }

  return areAllStopsCompleted.value ? 'stop-match' : 'stop-mismatch'
})

const completedIcon = computed(() => {
  if (!isJobCompleted.value) {
    return 'mdi-clock-outline'
  }

  return areAllStopsCompleted.value ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline'
})

const completedIconTooltip = computed(() => {
  if (!isJobCompleted.value) {
    return 'The job has not yet been completed.'
  } else if (areAllStopsCompleted.value) {
    return 'All stops were completed as requested.'
  } else if (!doAllLocationsMatch.value && !doesAllInventoryMatch.value) {
    return 'Inventory and location different than requested.'
  } else if (!doesAllInventoryMatch.value) {
    return 'Inventory different than requested.'
  } else if (!doAllLocationsMatch.value) {
    return 'Location different than requested.'
  } else {
    return ''
  }
})

const selected = computed({
  get() {
    return props.isSelected
  },
  set(selectedValue) {
    if (selectedValue) {
      selectJob(props.job.id)
    } else {
      deselectJob(props.job.id)
    }
  },
})

const jobTitle = computed(
  () => `#${props.job?.number} · ${tripNumber.value} of ${requestedTrips.value}`,
)
const resourceName = computed(() => props.job.assigned_resource_name || 'Unassigned')

const activityName = computed(() => props.job.activity_name)

function selectJob(jobId: string) {
  emit('selected', jobId)
}

function deselectJob(jobId: string) {
  emit('deselected', jobId)
}
</script>

<style scoped>
.stop-mismatch {
  background-color: var(--color-orange--lighter);
}
.stop-mismatch > .v-icon {
  color: var(--color-orange);
}

.stop-match {
  background-color: var(--color-green--lighter);
}
.stop-match > .v-icon {
  color: var(--color-green);
}

.job-not-complete {
  background-color: var(--color-blue--lighter);
}
.job-not-complete > .v-icon {
  color: var(--color-blue);
}
</style>
